import React, { useState, useRef } from 'react';
import {
  X,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Eye,
  FileCheck,
  Building2,
  Calendar,
  CreditCard,
  User,
  Home,
  ShieldCheck,
  Receipt,
  Printer,
  Download,
  Copy,
  Check,
  Loader2,
  Sparkles,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { CommonFeeBill } from '../types';
import { useApp } from '../context/AppContext';
import { ReceiptDocument } from './ReceiptDocument';

interface Props {
  bill: CommonFeeBill | null;
  onClose: () => void;
}

export const SlipVerificationModal: React.FC<Props> = ({ bill, onClose }) => {
  const { verifyBill, rejectBill, currentUser } = useApp();
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReasonPreset, setRejectReasonPreset] = useState('ยอดเงินโอนไม่ถูกต้องตามยอดในใบแจ้งหนี้');
  const [customReason, setCustomReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [successAction, setSuccessAction] = useState<'approved' | 'rejected' | null>(null);
  const [currentBillData, setCurrentBillData] = useState<CommonFeeBill | null>(bill);

  // PDF and Print State
  const receiptRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!bill) return null;

  const activeBill = currentBillData || bill;

  const handleApprove = async () => {
    setIsProcessing(true);
    const verifierName = currentUser?.full_name || 'คุณศศิธร สุขสมบูรณ์ (การเงินนิติบุคคล)';
    const updated = await verifyBill(bill.id, 'paid', verifierName);
    setIsProcessing(false);
    if (updated) {
      setCurrentBillData(updated);
    } else {
      const now = new Date().toISOString();
      const houseClean = bill.house_number.replace(/[^0-9]/g, '');
      const randomSuffix = Math.floor(1000 + Math.random() * 9000);
      setCurrentBillData({
        ...bill,
        status: 'paid',
        verified_by: verifierName,
        receipt_number: `RC-256908-${houseClean || '88'}-${randomSuffix}`,
        receipt_issued_at: now,
        receipt_issued_by: `${verifierName} (นิติบุคคล นราวดี อิลิแกนซ์)`,
      });
    }
    setSuccessAction('approved');
  };

  const handleReject = async () => {
    const finalReason = customReason.trim() ? customReason.trim() : rejectReasonPreset;
    setIsProcessing(true);
    const verifierName = currentUser?.full_name || 'เจ้าหน้าที่การเงินนิติบุคคล';
    await rejectBill(bill.id, finalReason, verifierName);
    setIsProcessing(false);
    setSuccessAction('rejected');
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  // Native Print
  const handlePrint = () => {
    window.print();
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    if (!receiptRef.current) return;
    try {
      setIsGeneratingPDF(true);

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgProps = pdf.getImageProperties(imgData);
      const ratio = imgProps.width / imgProps.height;
      
      const margin = 10;
      const printableWidth = pdfWidth - margin * 2;
      const printableHeight = printableWidth / ratio;
      const finalY = printableHeight < pdfHeight - margin * 2 ? margin + 5 : margin;

      pdf.addImage(imgData, 'JPEG', margin, finalY, printableWidth, printableHeight);

      const houseClean = activeBill.house_number.replace(/[\/\\]/g, '-');
      const periodClean = activeBill.period.replace(/\s+/g, '_');
      const fileName = `ใบเสร็จรับเงิน_นราวดี_${houseClean}_${periodClean}.pdf`;

      pdf.save(fileName);
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleCopyReceiptInfo = () => {
    const receiptNum = activeBill.receipt_number || `RC-256908-${activeBill.house_number.replace(/[^0-9]/g, '')}-4821`;
    const text = `ใบเสร็จรับเงินค่าส่วนกลาง โครงการนราวดี อิลิแกนซ์\nบ้านเลขที่: ${activeBill.house_number} (${activeBill.resident_name})\nงวด: ${activeBill.period}\nยอดชำระ: ฿${activeBill.amount.toLocaleString()} บาท\nเลขที่ใบเสร็จ: ${receiptNum}\nสถานะ: ชำระและออกใบเสร็จสมบูรณ์แล้ว`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/75 backdrop-blur-xs overflow-y-auto print:p-0 print:bg-white">
      <div
        className={`bg-white rounded-3xl w-full shadow-2xl border border-slate-100 overflow-hidden my-4 transition-all duration-300 flex flex-col print:border-none print:shadow-none print:m-0 print:rounded-none ${
          successAction === 'approved' ? 'max-w-4xl max-h-[92vh]' : 'max-w-2xl'
        }`}
      >
        {/* Header Bar */}
        <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between shrink-0 print:hidden">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-blue-600/30 text-blue-400 border border-blue-500/30">
              {successAction === 'approved' ? <Receipt className="w-5 h-5 text-emerald-400" /> : <FileCheck className="w-5 h-5" />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold">
                  {successAction === 'approved'
                    ? 'ใบเสร็จรับเงินอิเล็กทรอนิกส์ (E-Receipt)'
                    : 'ตรวจสอบและอนุมัติสลิปโอนเงิน'}
                </h3>
                {successAction === 'approved' && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold border border-emerald-500/30">
                    อนุมัติสำเร็จ
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400">
                บ้านเลขที่ {activeBill.house_number} • ประจำงวด {activeBill.period}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {successAction === 'approved' && (
              <>
                {/* Print button */}
                <button
                  onClick={handlePrint}
                  className="px-3.5 py-2 bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm shadow-blue-700/30 cursor-pointer"
                >
                  <Printer className="w-4 h-4" />
                  <span className="hidden sm:inline">พิมพ์ใบเสร็จ (Print)</span>
                </button>

                {/* PDF Save button */}
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm shadow-emerald-600/30 disabled:opacity-50 cursor-pointer"
                >
                  {isGeneratingPDF ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>สร้าง PDF...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Save PDF</span>
                    </>
                  )}
                </button>
              </>
            )}

            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors ml-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        {successAction === 'approved' ? (
          /* =========================================================================
             APPROVED STATE: Show Full Rendered Receipt with Print & PDF Save Actions
             ========================================================================= */
          <div className="flex flex-col flex-1 overflow-hidden bg-slate-100/90 print:bg-white">
            {/* Success notification banner above receipt */}
            <div className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex flex-wrap items-center justify-between gap-3 shadow-inner print:hidden shrink-0">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-white/20 rounded-full">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold">อนุมัติการชำระเงินเรียบร้อยแล้ว!</p>
                  <p className="text-[11px] text-emerald-100">
                    ออกใบเสร็จเลขที่ <strong className="font-mono text-white">{activeBill.receipt_number || 'RC-256908-8812-4821'}</strong> และอัปเดตยอดคงค้างลูกบ้านเป็น ฿0 แล้ว
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyReceiptInfo}
                  className="px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 backdrop-blur-xs"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-amber-300" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copied ? 'คัดลอกแล้ว!' : 'คัดลอกข้อมูล'}</span>
                </button>
              </div>
            </div>

            {/* Scrollable Receipt Area */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 print:p-0 print:overflow-visible">
              <ReceiptDocument bill={activeBill} receiptRef={receiptRef} />
            </div>

            {/* Bottom Footer Action Bar */}
            <div className="px-6 py-3.5 bg-white border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0 print:hidden">
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>บันทึกเข้าระบบนิติบุคคลแล้ว • ลูกบ้านสามารถดูและดาวน์โหลดใบเสร็จนี้ผ่านมือถือได้ทันที</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-xl transition-colors flex items-center gap-1.5"
                >
                  <Printer className="w-4 h-4 text-slate-600" />
                  <span>พิมพ์เอกสาร</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm shadow-emerald-600/20 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  <span>ดาวน์โหลด PDF</span>
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-colors"
                >
                  เสร็จสิ้น / ปิดหน้าต่าง
                </button>
              </div>
            </div>
          </div>
        ) : successAction === 'rejected' ? (
          /* =========================================================================
             REJECTED STATE: Feedback
             ========================================================================= */
          <div className="p-12 text-center space-y-4">
            <div className="w-16 h-16 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mx-auto animate-bounce">
              <XCircle className="w-10 h-10" />
            </div>
            <h4 className="text-xl font-bold text-slate-800">ปฏิเสธสลิปการชำระเงินเรียบร้อย</h4>
            <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
              ระบบได้บันทึกเหตุผลและส่งการแจ้งเตือนไปยังลูกบ้านบ้านเลขที่ {bill.house_number} เพื่อให้อัปโหลดสลิปใหม่อีกครั้ง
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-slate-800 transition-colors"
            >
              ปิดหน้าต่าง
            </button>
          </div>
        ) : (
          /* =========================================================================
             INITIAL REVIEW FORM: Slip Inspection, Resident Details, Approve/Reject
             ========================================================================= */
          <div className="p-6 space-y-6">
            {/* Slip Image & Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Slip Preview */}
              <div className="space-y-2">
                <span className="text-xs font-semibold text-slate-700 block">ภาพหลักฐานการโอน (สลิป):</span>
                <div className="relative rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden group shadow-inner min-h-[280px] flex items-center justify-center">
                  {bill.slip_url ? (
                    <>
                      <img
                        src={bill.slip_url}
                        alt="Slip evidence"
                        className="w-full h-auto max-h-[320px] object-contain rounded-lg transition-transform duration-300 group-hover:scale-105"
                      />
                      <a
                        href={bill.slip_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-3 right-3 bg-black/75 hover:bg-black text-white text-xs px-3 py-1.5 rounded-xl flex items-center gap-1.5 shadow-md backdrop-blur-xs transition-colors font-medium"
                      >
                        <Eye className="w-3.5 h-3.5" /> ดูภาพขนาดเต็ม
                      </a>
                    </>
                  ) : (
                    <div className="text-center p-6 text-slate-400">
                      <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">ไม่มีรูปภาพสลิปที่แนบมา</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Bill & Resident Details */}
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2.5 text-xs">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                    <span className="text-slate-500">สถานะปัจจุบัน:</span>
                    <span className="px-2.5 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                      รอการตรวจสอบสลิป
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 flex items-center gap-1">
                      <Home className="w-3.5 h-3.5 text-slate-400" /> บ้านเลขที่:
                    </span>
                    <span className="font-bold text-slate-800 text-sm font-mono">{bill.house_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 flex items-center gap-1">
                      <User className="w-3.5 h-3.5 text-slate-400" /> ชื่อเจ้าของบ้าน:
                    </span>
                    <span className="font-medium text-slate-800">{bill.resident_name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" /> งวดค่าส่วนกลาง:
                    </span>
                    <span className="font-medium text-slate-800">{bill.period}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-slate-400" /> วันเวลาที่โอน:
                    </span>
                    <span className="font-medium text-slate-700">
                      {bill.paid_at ? new Date(bill.paid_at).toLocaleString('th-TH') : 'วันนี้'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                    <span className="text-slate-700 font-semibold text-sm">ยอดที่ต้องชำระ:</span>
                    <span className="font-extrabold text-base text-blue-700 font-mono">
                      ฿{bill.amount.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Account match guide */}
                <div className="p-3.5 rounded-2xl bg-blue-50/70 border border-blue-100 text-xs text-blue-900 flex items-start gap-2.5">
                  <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="font-bold text-blue-950">บัญชีนิติบุคคลรับโอน (ตรวจสอบปลายทาง):</p>
                    <p className="text-blue-800 font-mono">
                      ธ.กสิกรไทย • 123-4-56789-0 (นิติบุคคล นราวดี อิลิแกนซ์)
                    </p>
                    <p className="text-[11px] text-blue-600">เมื่ออนุมัติ ระบบจะสร้างใบเสร็จรับเงินพร้อมปุ่มพิมพ์และบันทึก PDF ทันที</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reject Reason Form if opened */}
            {rejectMode && (
              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 space-y-3 animate-in fade-in">
                <div className="flex items-center gap-2 text-rose-900 font-bold text-sm">
                  <AlertCircle className="w-4 h-4 text-rose-600" />
                  ระบุเหตุผลการปฏิเสธสลิป:
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs text-rose-800 block font-medium">เลือกเหตุผลที่พบบ่อย:</label>
                  <select
                    value={rejectReasonPreset}
                    onChange={(e) => setRejectReasonPreset(e.target.value)}
                    className="w-full text-xs p-2.5 rounded-xl border border-rose-300 bg-white text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-rose-400"
                  >
                    <option value="ยอดเงินโอนไม่ถูกต้องตามยอดในใบแจ้งหนี้">ยอดเงินโอนไม่ถูกต้องตามยอดในใบแจ้งหนี้</option>
                    <option value="รูปภาพสลิปไม่ชัดเจน / เบลอ / ไม่สามารถอ่าน QR Code ได้">รูปภาพสลิปไม่ชัดเจน / เบลอ / ไม่สามารถอ่าน QR Code ได้</option>
                    <option value="สลิปซ้ำซ้อนกับงวดก่อนหน้า">สลิปซ้ำซ้อนกับงวดก่อนหน้า</option>
                    <option value="บัญชีปลายทางไม่ใช่นิติบุคคลหมู่บ้านนราวดี อิลิแกนซ์">บัญชีปลายทางไม่ใช่นิติบุคคลหมู่บ้านนราวดี อิลิแกนซ์</option>
                    <option value="ระบุเหตุผลอื่นๆ ด้านล่าง">ระบุเหตุผลอื่นๆ ด้านล่าง</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs text-rose-800 block mb-1 font-medium">หมายเหตุเพิ่มเติม (จะส่งแจ้งเตือนให้ลูกบ้าน):</label>
                  <input
                    type="text"
                    placeholder="เช่น ยอดขาด 100 บาท หรือขอสลิปฉบับเต็มที่มี QR Code ชัดเจน"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    className="w-full text-xs p-2.5 rounded-xl border border-rose-300 bg-white text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-rose-400"
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100 gap-3">
              {!rejectMode ? (
                <>
                  <button
                    type="button"
                    onClick={() => setRejectMode(true)}
                    className="px-4 py-2.5 rounded-xl border border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <XCircle className="w-4 h-4 text-rose-600" />
                    ปฏิเสธสลิป (Reject)
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-medium transition-colors"
                    >
                      ปิดหน้าต่าง
                    </button>
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={isProcessing}
                      className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-2 shadow-md shadow-emerald-600/20 transition-all disabled:opacity-50 cursor-pointer"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>กำลังอนุมัติ...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>อนุมัติสลิป & ออกใบเสร็จ (Approve)</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setRejectMode(false)}
                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-medium transition-colors"
                  >
                    ย้อนกลับ
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={isProcessing}
                    className="px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-md shadow-rose-600/20 transition-all disabled:opacity-50"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>กำลังบันทึก...</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4" />
                        <span>ยืนยันปฏิเสธสลิป</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
